import { ref, readonly, type Ref } from "vue";
import { useToast } from "primevue/usetoast";
import { deleteUser } from "../api";

interface UseArchiveUserReturn {
  readonly isArchiving: Ref<boolean>;
  archiveUser: (userId: number, organizationId?: number) => Promise<boolean>;
}

/**
 * Composable for archiving (deleting/deactivating) a user
 */
export function useArchiveUser(): UseArchiveUserReturn {
  const toast = useToast();
  const isArchiving = ref(false);

  async function archiveUser(
    userId: number,
    organizationId?: number
  ): Promise<boolean> {
    if (!userId) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "User ID is required",
        life: 5000,
      });
      return false;
    }

    isArchiving.value = true;

    try {
      const result = await deleteUser(userId, organizationId);

      // Check for error (result could be undefined/null for 204 responses)
      if (result && typeof result === "object" && "error" in result) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: result.error || "Failed to archive user",
          life: 5000,
        });
        return false;
      }

      // If we get here, the delete was successful (204 or other success)
      toast.add({
        severity: "success",
        summary: "Success",
        detail: "User has been archived successfully",
        life: 3000,
      });

      return true;
    } catch (error) {
      console.error("Error archiving user:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "An unexpected error occurred",
        life: 5000,
      });
      return false;
    } finally {
      isArchiving.value = false;
    }
  }

  return {
    isArchiving: readonly(isArchiving),
    archiveUser,
  };
}
